import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    Row,
    Col,
    Card,
    Button,
    Spinner,
    Alert,
    Form,
    Modal,
} from "react-bootstrap";

import axios from "axios";
import PageLayout from "../../layouts/PageLayout";
import { Link } from "react-router-dom";

import Select from "react-select";

export default function AssignProductsToListerPage() {
    // =========================================================
    // STATE
    // =========================================================

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // API grouped lister data
    const [listerGroups, setListerGroups] = useState([]);

    // Selected lister
    const [selectedListerId, setSelectedListerId] =
        useState("");

    // Selected products
    const [selectedProducts, setSelectedProducts] =
        useState([]);

    // Handover photo
    const [handoverImage, setHandoverImage] =
        useState(null);

    const [imagePreview, setImagePreview] =
        useState(null);

    // Camera
    const [cameraOpen, setCameraOpen] =
        useState(false);

    const [cameraStream, setCameraStream] =
        useState(null);

    const videoRef = useRef(null);

    // =========================================================
    // FETCH PRODUCTS
    // =========================================================

    useEffect(() => {
        fetchProducts();
    }, []);

    // =========================================================
    // CAMERA CLEANUP
    // =========================================================

    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    // =========================================================
    // FETCH LISTER PRODUCTS
    // =========================================================

    const fetchProducts = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/products/new`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "authToken"
                        )}`,
                    },
                }
            );

            if (response.data.success) {
                setListerGroups(
                    response.data.data || []
                );
            } else {
                setError(
                    response.data.message ||
                    "Unable to load products."
                );
            }
        } catch (err) {
            console.error(
                "Error loading products:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Failed to load products."
            );
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // SELECT LISTER
    // =========================================================

    const handleListerChange = (e) => {
        const listerId = e.target.value;

        setSelectedListerId(listerId);

        // Clear selected products when lister changes
        setSelectedProducts([]);

        // Clear previous photo
        removeHandoverImage();

        setSuccess(null);
        setError(null);
    };

    // =========================================================
    // GET CURRENT LISTER GROUP
    // =========================================================

    const selectedListerGroup =
        listerGroups.find(
            (group) =>
                String(group.lister?.id) ===
                String(selectedListerId)
        );

    const currentProducts =
        selectedListerGroup?.lister_products || [];

    // =========================================================
    // SELECT / UNSELECT PRODUCT
    // =========================================================

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) => {
            if (prev.includes(productId)) {
                return prev.filter(
                    (id) => id !== productId
                );
            }

            return [
                ...prev,
                productId,
            ];
        });
    };

    // =========================================================
    // SELECT ALL
    // =========================================================

    const selectAllProducts = () => {
        const ids = currentProducts.map(
            (product) => product.id
        );

        setSelectedProducts(ids);
    };

    // =========================================================
    // UNSELECT ALL
    // =========================================================

    const clearSelectedProducts = () => {
        setSelectedProducts([]);
    };

    // =========================================================
    // NORMAL IMAGE UPLOAD
    // =========================================================

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {
            alert(
                "Please select a valid image."
            );

            return;
        }

        setHandoverImage(file);

        setImagePreview(
            URL.createObjectURL(file)
        );
    };

    // =========================================================
    // OPEN CAMERA
    // =========================================================

    const openCamera = async () => {
        try {
            setError(null);

            if (
                !navigator.mediaDevices ||
                !navigator.mediaDevices.getUserMedia
            ) {
                alert(
                    "Camera is not supported by this browser."
                );

                return;
            }

            stopCamera();

            const stream =
                await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: {
                            ideal: "environment",
                        },

                        width: {
                            ideal: 1920,
                        },

                        height: {
                            ideal: 1080,
                        },
                    },

                    audio: false,
                });

            setCameraStream(stream);
            setCameraOpen(true);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject =
                        stream;

                    videoRef.current
                        .play()
                        .catch((err) => {
                            console.error(
                                "Video play error:",
                                err
                            );
                        });
                }
            }, 100);
        } catch (err) {
            console.error(
                "Camera error:",
                err
            );

            if (
                err.name ===
                "NotAllowedError"
            ) {
                alert(
                    "Camera permission was denied. Please allow camera access."
                );
            } else if (
                err.name ===
                "NotFoundError"
            ) {
                alert(
                    "No camera was found on this device."
                );
            } else {
                alert(
                    "Unable to open camera."
                );
            }
        }
    };

    // =========================================================
    // STOP CAMERA
    // =========================================================

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream
                .getTracks()
                .forEach((track) => {
                    track.stop();
                });
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setCameraStream(null);
        setCameraOpen(false);
    };

    // =========================================================
    // CAPTURE PHOTO
    // =========================================================

    const capturePhoto = () => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        if (
            !video.videoWidth ||
            !video.videoHeight
        ) {
            alert(
                "Camera is not ready. Please wait."
            );

            return;
        }

        const canvas =
            document.createElement("canvas");

        canvas.width =
            video.videoWidth;

        canvas.height =
            video.videoHeight;

        const context =
            canvas.getContext("2d");

        context.drawImage(
            video,
            0,
            0,
            canvas.width,
            canvas.height
        );

        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    alert(
                        "Unable to capture photo."
                    );

                    return;
                }

                const file = new File(
                    [blob],
                    `lister-handover-${Date.now()}.jpg`,
                    {
                        type: "image/jpeg",
                    }
                );

                setHandoverImage(file);

                setImagePreview(
                    URL.createObjectURL(file)
                );

                stopCamera();
            },
            "image/jpeg",
            0.9
        );
    };

    // =========================================================
    // REMOVE IMAGE
    // =========================================================

    const removeHandoverImage = () => {
        setHandoverImage(null);
        setImagePreview(null);
    };

    // =========================================================
    // ASSIGN PRODUCTS
    // =========================================================

    const handleAssignProducts = async () => {
        setError(null);
        setSuccess(null);

        // Validate lister
        if (!selectedListerId) {
            setError(
                "Please select a lister."
            );

            return;
        }

        // Validate products
        if (
            selectedProducts.length === 0
        ) {
            setError(
                "Please select at least one product."
            );

            return;
        }

        // Validate photo
        if (!handoverImage) {
            setError(
                "Please take a handover photo before assigning the products."
            );

            return;
        }

        setSubmitting(true);

        try {
            const formData =
                new FormData();

            formData.append(
                "lister_id",
                selectedListerId
            );

            selectedProducts.forEach(
                (productId) => {
                    formData.append(
                        "product_ids[]",
                        productId
                    );
                }
            );

            formData.append(
                "handover_image",
                handoverImage
            );

            const response =
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/issuer/products/assign-to-lister`,
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",

                            Authorization: `Bearer ${localStorage.getItem(
                                "authToken"
                            )}`,
                        },
                    }
                );

            if (response.data.success) {
                setSuccess(
                    response.data.message ||
                    "Products assigned successfully."
                );

                setSelectedProducts([]);

                removeHandoverImage();

                // Reload products
                await fetchProducts();
            } else {
                setError(
                    response.data.message ||
                    "Unable to assign products."
                );
            }
        } catch (err) {
            console.error(
                "Assignment error:",
                err
            );

            if (
                err.response?.status === 422
            ) {
                const validationErrors =
                    err.response.data.errors;

                if (validationErrors) {
                    const messages =
                        Object.values(
                            validationErrors
                        )
                            .flat()
                            .join(" ");

                    setError(messages);
                } else {
                    setError(
                        err.response.data.message ||
                        "Validation failed."
                    );
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to assign products."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />

                    <p className="mt-3">
                        Loading products...
                    </p>
                </div>
            </PageLayout>
        );
    }

    const listerSelectOptions = listerGroups
        .filter((group) => group.lister)
        .map((group) => {
            const nickname =
                group.lister_products?.[0]?.lister?.nickname;

            return {
                value: String(group.lister.id),
                label: nickname
                    ? `${group.lister.name} (${nickname})`
                    : group.lister.name,
            };
        });

    // =========================================================
    // PAGE
    // =========================================================

    return (
        <PageLayout>
            <Row>

                {/* =========================================
            BREADCRUMB
        ========================================== */}

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">

                            <h3 className="mc-breadcrumb-title">
                                Give Products to Lister
                            </h3>

                            <ul className="mc-breadcrumb-list">

                                <li className="mc-breadcrumb-item">
                                    <Link
                                        to="#"
                                        className="mc-breadcrumb-link"
                                    >
                                        Home
                                    </Link>
                                </li>

                                <li className="mc-breadcrumb-item">
                                    Products
                                </li>

                                <li className="mc-breadcrumb-item">
                                    Give to Lister
                                </li>

                            </ul>

                        </div>
                    </div>
                </Col>

                {/* =========================================
            ALERTS
        ========================================== */}

                <Col xl={12}>

                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() =>
                                setError(null)
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert
                            variant="success"
                            dismissible
                            onClose={() =>
                                setSuccess(null)
                            }
                        >
                            {success}
                        </Alert>
                    )}

                </Col>

                {/* =========================================
            LISTER SELECT
        ========================================== */}

                <Col xl={12}>
                    <div className="mc-card mb-4">

                        <h4 className="mb-3">
                            Select Lister
                        </h4>

                        <Form.Group>
                            <Form.Label>Lister</Form.Label>

                            <Select
                                isClearable
                                isSearchable
                                placeholder="Select Lister"
                                options={listerSelectOptions}
                                value={
                                    listerSelectOptions.find(
                                        (opt) => opt.value === String(selectedListerId)
                                    ) || null
                                }
                                onChange={(selectedOption) => {
                                    // handleListerChange ke structure ke mutabiq object pass karein
                                    const value = selectedOption ? selectedOption.value : "";
                                    handleListerChange({ target: { value } });
                                }}
                            />
                        </Form.Group>

                    </div>
                </Col>

                {/* =========================================
            PRODUCTS
        ========================================== */}

                {selectedListerId && (
                    <Col xl={12}>

                        <div className="mc-card mb-4">

                            <div
                                className="d-flex justify-content-between align-items-center mb-4"
                            >

                                <div>
                                    <h4 className="mb-1">
                                        Products
                                    </h4>

                                    <small className="text-muted">
                                        {
                                            selectedListerGroup
                                                ?.lister?.name
                                        }
                                        {" — "}
                                        {currentProducts.length}{" "}
                                        products
                                    </small>
                                </div>

                                <div>

                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={
                                            selectAllProducts
                                        }
                                    >
                                        Select All
                                    </Button>

                                    <Button
                                        variant="secondary"
                                        className="me-2"
                                        size="sm"
                                        onClick={
                                            clearSelectedProducts
                                        }
                                    >
                                        Clear
                                    </Button>

                                </div>

                            </div>

                            {currentProducts.length ===
                                0 ? (
                                <Alert variant="info">
                                    No products available
                                    for this lister.
                                </Alert>
                            ) : (
                                <Row>

                                    {currentProducts.map(
                                        (product) => {

                                            const selected =
                                                selectedProducts.includes(
                                                    product.id
                                                );

                                            return (
                                                <Col
                                                    key={
                                                        product.id
                                                    }
                                                    xs={12}
                                                    sm={6}
                                                    md={4}
                                                    lg={3}
                                                    className="mb-4"
                                                >

                                                    <Card
                                                        className={
                                                            selected
                                                                ? "h-100 border-primary"
                                                                : "h-100"
                                                        }
                                                        style={{
                                                            cursor:
                                                                "pointer",
                                                            borderWidth:
                                                                selected
                                                                    ? "2px"
                                                                    : "1px",
                                                        }}
                                                        onClick={() =>
                                                            toggleProduct(
                                                                product.id
                                                            )
                                                        }
                                                    >

                                                        {/* IMAGE */}

                                                        <div
                                                            style={{
                                                                height:
                                                                    "180px",
                                                                background:
                                                                    "#f8f9fa",
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                overflow:
                                                                    "hidden",
                                                            }}
                                                        >

                                                            {product.first_img ? (
                                                                <img
                                                                    src={
                                                                        process.env.REACT_APP_IMG_URL + product.first_img
                                                                    }
                                                                    alt={
                                                                        product.title
                                                                    }
                                                                    style={{
                                                                        width:
                                                                            "100%",
                                                                        height:
                                                                            "100%",
                                                                        objectFit:
                                                                            "contain",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted">
                                                                    No Image
                                                                </span>
                                                            )}

                                                        </div>

                                                        <Card.Body>

                                                            <div className="d-flex align-items-start">

                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={
                                                                        selected
                                                                    }
                                                                    onChange={() =>
                                                                        toggleProduct(
                                                                            product.id
                                                                        )
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                    className="me-2"
                                                                />

                                                                <div>

                                                                    <h6 className="mb-1">
                                                                        {
                                                                            product.title
                                                                        }
                                                                    </h6>

                                                                    <small className="text-muted">
                                                                        {
                                                                            product.product_code
                                                                        }
                                                                    </small>

                                                                </div>

                                                            </div>

                                                            <div className="mt-3">

                                                                <small>
                                                                    Category:{" "}
                                                                    <strong>
                                                                        {
                                                                            product
                                                                                .category
                                                                                ?.name
                                                                        }
                                                                    </strong>
                                                                </small>

                                                                <br />

                                                                <small>
                                                                    Selling Style:{" "}
                                                                    <strong>
                                                                        {
                                                                            product.selling_style
                                                                        }
                                                                    </strong>
                                                                </small>

                                                                <br />

                                                                <strong className="d-block mt-2">
                                                                    $
                                                                    {
                                                                        product.sell_price_dollar
                                                                    }
                                                                </strong>

                                                            </div>

                                                        </Card.Body>

                                                    </Card>

                                                </Col>
                                            );
                                        }
                                    )}

                                </Row>
                            )}

                        </div>

                    </Col>
                )}

                {/* =========================================
            HANDOVER SECTION
        ========================================== */}

                {selectedListerId &&
                    selectedProducts.length >
                    0 && (

                        <Col xl={12}>

                            <div className="mc-card mb-4">

                                <h4 className="mb-1">
                                    Product Handover
                                </h4>

                                <p className="text-muted mb-4">
                                    Take a photo as proof when
                                    handing these products to the
                                    lister.
                                </p>

                                <Row>

                                    {/* PHOTO */}

                                    <Col
                                        md={6}
                                        className="mb-3"
                                    >

                                        <div
                                            style={{
                                                border:
                                                    "2px dashed #ddd",
                                                borderRadius:
                                                    "10px",
                                                padding:
                                                    "20px",
                                                textAlign:
                                                    "center",
                                            }}
                                        >

                                            {imagePreview ? (
                                                <>

                                                    <img
                                                        src={
                                                            imagePreview
                                                        }
                                                        alt="Handover"
                                                        style={{
                                                            width:
                                                                "100%",
                                                            maxHeight:
                                                                "350px",
                                                            objectFit:
                                                                "contain",
                                                            borderRadius:
                                                                "8px",
                                                        }}
                                                    />

                                                    <div className="mt-3">

                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={
                                                                removeHandoverImage
                                                            }
                                                        >
                                                            Remove Photo
                                                        </Button>

                                                        <Button
                                                            variant="dark"
                                                            size="sm"
                                                            onClick={
                                                                openCamera
                                                            }
                                                        >
                                                            Retake Photo
                                                        </Button>

                                                    </div>

                                                </>
                                            ) : (
                                                <>

                                                    <div
                                                        style={{
                                                            fontSize:
                                                                "50px",
                                                            marginBottom:
                                                                "15px",
                                                        }}
                                                    >
                                                        📷
                                                    </div>

                                                    <h5>
                                                        Handover Photo
                                                    </h5>

                                                    <p className="text-muted">
                                                        Take a photo of the
                                                        product handover
                                                    </p>

                                                    <Button
                                                        variant="primary"
                                                        className="me-2"
                                                        onClick={
                                                            openCamera
                                                        }
                                                    >
                                                        📷 Take Photo
                                                    </Button>

                                                    {/* <label
                                                        className="btn btn-secondary"
                                                    >
                                                        Upload Photo

                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            hidden
                                                            onChange={
                                                                handleImageUpload
                                                            }
                                                        />
                                                    </label> */}

                                                </>
                                            )}

                                        </div>

                                    </Col>

                                    {/* SUMMARY */}

                                    <Col md={6}>

                                        <div
                                            style={{
                                                background:
                                                    "#f8f9fa",
                                                borderRadius:
                                                    "10px",
                                                padding:
                                                    "20px",
                                            }}
                                        >

                                            <h5>
                                                Handover Summary
                                            </h5>

                                            <hr />

                                            <div className="mb-3">
                                                <small className="text-muted">
                                                    Lister
                                                </small>

                                                <h6>
                                                    {selectedListerGroup?.lister?.name}
                                                    {selectedListerGroup?.lister_products?.[0]?.lister?.nickname && (
                                                        <> ({selectedListerGroup.lister_products[0].lister.nickname})</>
                                                    )}
                                                </h6>
                                            </div>

                                            <div className="mb-3">
                                                <small className="text-muted">
                                                    Selected Products
                                                </small>

                                                <h3>
                                                    {
                                                        selectedProducts.length
                                                    }
                                                </h3>
                                            </div>

                                            <div className="mb-3">
                                                <small className="text-muted">
                                                    Handover Photo
                                                </small>

                                                <h6>
                                                    {handoverImage
                                                        ? "✓ Photo captured"
                                                        : "⚠ Photo required"}
                                                </h6>
                                            </div>

                                            <Button
                                                variant="success"
                                                className="w-100"
                                                size="lg"
                                                disabled={
                                                    submitting ||
                                                    !handoverImage
                                                }
                                                onClick={
                                                    handleAssignProducts
                                                }
                                            >
                                                {submitting ? (
                                                    <>
                                                        <Spinner
                                                            size="sm"
                                                            className="me-2"
                                                        />

                                                        Assigning...
                                                    </>
                                                ) : (
                                                    <>
                                                        ✓ Give Products to Lister
                                                    </>
                                                )}
                                            </Button>

                                        </div>

                                    </Col>

                                </Row>

                            </div>

                        </Col>
                    )}

            </Row>

            {/* =====================================================
          CAMERA MODAL
      ====================================================== */}

            <Modal
                show={cameraOpen}
                onHide={stopCamera}
                centered
                size="lg"
                backdrop="static"
            >

                <Modal.Header
                    closeButton
                    closeVariant="white"
                    style={{
                        background: "#000",
                        color: "#fff",
                    }}
                >

                    <Modal.Title>
                        Take Handover Photo
                    </Modal.Title>

                </Modal.Header>

                <Modal.Body
                    style={{
                        background: "#000",
                        padding: 0,
                    }}
                >

                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                            width: "100%",
                            maxHeight: "70vh",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />

                </Modal.Body>

                <Modal.Footer
                    style={{
                        background: "#000",
                    }}
                >

                    <Button
                        variant="secondary"
                        onClick={
                            stopCamera
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        onClick={
                            capturePhoto
                        }
                    >
                        📸 Capture Photo
                    </Button>

                </Modal.Footer>

            </Modal>

        </PageLayout>
    );
}
